/**
 * The two decisions `useUrlState` makes, as functions that can be tested.
 *
 * The hook itself is a few lines of `useSearchParams` around these. It is kept
 * that thin on purpose: the unit test config here runs in `node` with no DOM,
 * so anything living inside the hook body is untested by construction, and the
 * things worth pinning are exactly the ones below.
 */
/**
 * A query parameter as a typed value, or the default.
 *
 * **A URL is user input.** `?view=nonsense` is one keystroke away from any
 * user and guaranteed from any crawler, and a component that renders whatever
 * the query string says will eventually push `undefined` through a switch with
 * no default branch. Falling back is the right failure: a bad parameter should
 * degrade the view, never break the page.
 */
export function parseUrlValue(raw, schema, fallback) {
    if (raw === null)
        return fallback;
    const parsed = schema.safeParse(raw);
    return parsed.success ? parsed.data : fallback;
}
/**
 * The next query string after setting one key.
 *
 * **The default is expressed by the parameter's absence.** Writing
 * `?view=card` when card is already the default makes every URL in the product
 * longer for no information, and a page whose defaults are all spelled out is
 * one nobody wants to paste into a chat window. Setting a value back to the
 * default therefore removes the key rather than writing it.
 *
 * Every other parameter is preserved: this is a query string with a workspace
 * filter and a tab in it, not a field this component owns.
 */
export function nextSearchParams(current, key, next, fallback) {
    const params = new URLSearchParams(current);
    if (next === fallback) {
        params.delete(key);
    }
    else {
        params.set(key, String(next));
    }
    return params;
}
/**
 * Folds every URL write made in one tick into the last one.
 *
 * **react-router's functional `setSearchParams` does not queue.** Unlike
 * `useState`, its `(prev) => next` form is handed the params *as of the last
 * render*, not as of the previous call — so two setters fired by one handler
 * each start from the same stale URL and the second navigation silently
 * discards the first. Every filter on the Costing page did exactly that: the
 * filter was written, then `setPage(1)` rewrote the old query string over it,
 * and the dropdown snapped back with nothing thrown.
 *
 * The batcher remembers what this tick has already written and builds each
 * later write on top of it, so the final navigation carries all of them. It
 * forgets at the end of the tick, by which point the router has the result.
 */
export function createSearchParamsBatcher(schedule = queueMicrotask) {
    let pending = null;
    return (rendered, update) => {
        if (pending === null) {
            schedule(() => {
                pending = null;
            });
        }
        pending = update(pending ?? rendered);
        return pending;
    };
}
//# sourceMappingURL=url_state.js.map